import { useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ApiError } from "../api/client";
import { Logo } from "../components/ui/Logo";
import { AccessRequestModal } from "./requests/AccessRequestModal";

export default function Login() {
  const { user, login, loading } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);

  if (!loading && user) return <Navigate to="/" replace />;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(username, password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Anmeldung fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center bg-surface px-4 overflow-hidden">
      {/* Atmosphärischer Hintergrund: Vignette, Camouflage-Flecken, Carbon-Raster */}
      <div
        className="absolute inset-0 bg-camo"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 70% 55% at 50% 30%, rgba(255,204,51,0.06), transparent 65%), radial-gradient(ellipse 90% 70% at 50% 100%, rgba(255,140,26,0.12), transparent 60%)",
        }}
      />
      <div className="absolute inset-0 bg-carbon opacity-40" />
      <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 160px rgba(0,0,0,0.85)" }} />

      <div className="relative w-full max-w-3xl">
        <div className="flex flex-col items-center mb-10">
          <Logo eager animated className="h-56 sm:h-64 w-auto mb-2" />
          <p className="font-display text-sm sm:text-base text-sand uppercase tracking-[0.35em] mt-2">
            LBV-System
          </p>
          <p className="text-xs text-gray-600 italic mt-3 tracking-wide">by xVayturah</p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch justify-center gap-6">
          <div className="w-full max-w-sm mx-auto sm:mx-0">
            <form onSubmit={handleSubmit} className="card p-6 space-y-4">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sand-muted/40 to-transparent" />

              {error && (
                <div className="bg-danger-muted text-danger text-sm rounded px-3 py-2 border border-danger/30">
                  {error}
                </div>
              )}

              <div>
                <label className="label">Benutzername</label>
                <input
                  className="input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Benutzername"
                  autoComplete="username"
                  autoFocus
                  required
                />
              </div>
              <div>
                <label className="label">Passwort</label>
                <input
                  className="input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>

              <button type="submit" disabled={submitting} className="btn-primary w-full py-2.5 text-base">
                {submitting ? "Wird geprüft…" : "Zutritt anfordern"}
              </button>
            </form>
          </div>

          {/* "Zugang anfragen" - rechts neben dem Login-Fenster (Desktop), darunter gestapelt (Mobile). */}
          <div className="w-full sm:w-60 mx-auto sm:mx-0 card p-6 flex flex-col items-center justify-center text-center gap-3">
            <p className="text-sm text-gray-400">Noch kein Zugang zum LBV-System?</p>
            <button type="button" className="btn-secondary w-full" onClick={() => setRequestOpen(true)}>
              Zugang anfragen
            </button>
          </div>
        </div>
      </div>

      {requestOpen && <AccessRequestModal onClose={() => setRequestOpen(false)} />}
    </div>
  );
}
